/**
 * Photo Job Details tab — renamed from PhotographyTab.jsx to escape the
 * Emergent deploy pipeline cache which was randomly stripping one import
 * line per deploy from the old filename, producing a blank screen.
 */
import * as React from 'react';
import * as lucide from 'lucide-react';
import { Button as UIButton } from '../ui/button';
import { SalesField, DateField } from './SalesTab';
import { PhotographerField } from './PhotographerField';
import { CFCardField } from './CFCardField';
import { SelectField } from './FieldOptions';
import { ErrorBoundary } from '../ErrorBoundary';

const { memo } = React;
const Save = lucide.Save;
const Loader2 = lucide.Loader2;

export const PhotoJobDetails = memo(({
  salesYear,
  salesData,
  salesLoading,
  salesDirty,
  savingSales,
  saveSalesData,
  updateSalesFormRef,
}) => {
  if (salesLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        {Loader2 ? <Loader2 className="w-6 h-6 animate-spin" /> : <span>Loading…</span>}
      </div>
    );
  }

  const data = salesData || {};

  return (
    <ErrorBoundary label="Photo tab">
      <div
        className="space-y-4"
        key={`photo-${data.org_id || 'unknown'}-${salesYear}-${data.sales_id || 'new'}`}
      >
        <div className="flex justify-end">
          <UIButton
            size="sm"
            onClick={saveSalesData}
            disabled={!salesDirty || savingSales}
            className={salesDirty ? 'animate-pulse' : undefined}
            data-testid="photo-tab-save-btn"
          >
            {savingSales
              ? (Loader2 ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <span className="mr-1">…</span>)
              : (Save ? <Save className="w-3 h-3 mr-1" /> : null)}
            {salesDirty ? 'Save Changes' : 'Saved'}
          </UIButton>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-1 bg-green-50/50 dark:bg-green-950/20 rounded-lg p-3">
            <h5 className="text-xs font-semibold text-green-700 dark:text-green-400 mb-2 uppercase tracking-wide">
              Photographer Assignment
            </h5>
            <SalesField label="Photography Request" field="photography_request" defaultValue={data.photography_request} onFieldChange={updateSalesFormRef} />
            <PhotographerField label="Photographer Day 1" field="photographer_day_1" defaultValue={data.photographer_day_1} onFieldChange={updateSalesFormRef} />
            <PhotographerField label="Photographer Day 2" field="photographer_day_2" defaultValue={data.photographer_day_2} onFieldChange={updateSalesFormRef} />
            <PhotographerField label="Photographer Day 3" field="photographer_day_3" defaultValue={data.photographer_day_3} onFieldChange={updateSalesFormRef} />
            <PhotographerField label="Photographer Day 4" field="photographer_day_4" defaultValue={data.photographer_day_4} onFieldChange={updateSalesFormRef} />
            <PhotographerField label="Photographer Day 5" field="photographer_day_5" defaultValue={data.photographer_day_5} onFieldChange={updateSalesFormRef} />
            <PhotographerField label="Photographer Day 6" field="photographer_day_6" defaultValue={data.photographer_day_6} onFieldChange={updateSalesFormRef} />
          </div>
          <div className="space-y-1 bg-blue-50/50 dark:bg-blue-950/20 rounded-lg p-3">
            <h5 className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-2 uppercase tracking-wide">
              Job Details
            </h5>
            <CFCardField label="CF Cards Allocated" field="cf_cards_allocated" defaultValue={data.cf_cards_allocated} onFieldChange={updateSalesFormRef} />
            <DateField label="Job Confirmed" field="job_confirmed" defaultValue={data.job_confirmed} onFieldChange={updateSalesFormRef} />
            <SalesField label="Photography Special Inst" field="photography_special_instructions" defaultValue={data.photography_special_instructions} onFieldChange={updateSalesFormRef} />
            <SelectField label="Schedule Received" field="schedule_received" defaultValue={data.schedule_received} onFieldChange={updateSalesFormRef} />
            <SelectField label="Names Received" field="names_received" defaultValue={data.names_received} onFieldChange={updateSalesFormRef} />
            <SalesField label="Team Names Received" field="team_names_received" defaultValue={data.team_names_received} onFieldChange={updateSalesFormRef} />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
});

PhotoJobDetails.displayName = 'PhotoJobDetails';

// Back-compat alias so existing imports of `PhotographyTab` keep working.
export const PhotographyTab = PhotoJobDetails;

export default PhotoJobDetails;
